import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComparisonTool = ({ currentService, similarServices }) => {
  const [selectedServices, setSelectedServices] = useState([currentService?.id]);
  const [showComparison, setShowComparison] = useState(false);

  const toggleServiceSelection = (serviceId) => {
    if (selectedServices?.includes(serviceId)) {
      setSelectedServices(selectedServices?.filter(id => id !== serviceId));
    } else if (selectedServices?.length < 3) {
      setSelectedServices([...selectedServices, serviceId]);
    }
  };

  const allServices = [currentService, ...similarServices];
  const servicesToCompare = allServices?.filter(service => 
    selectedServices?.includes(service?.id)
  );

  const comparisonFeatures = [
    'Implementation Time',
    'Starting Price',
    'Support Level',
    'Scalability',
    'Integration Options',
    'Training Included',
    'Warranty Period',
    'Customization Level'
  ];

  return (
    <div className="bg-card border-t border-border py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">Compare Services</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Compare features and benefits to find the perfect solution for your needs
          </p>
        </div>

        {!showComparison ? (
          <div>
            {/* Service Selection */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Select services to compare (up to 3)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allServices?.map((service) => (
                  <div
                    key={service?.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-smooth ${
                      selectedServices?.includes(service?.id)
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                    onClick={() => toggleServiceSelection(service?.id)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        {service?.category}
                      </span>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        selectedServices?.includes(service?.id)
                          ? 'border-primary bg-primary' :'border-muted-foreground'
                      }`}>
                        {selectedServices?.includes(service?.id) && (
                          <Icon name="Check" size={12} className="text-primary-foreground" />
                        )}
                      </div>
                    </div>
                    
                    <h4 className="font-medium text-foreground mb-2 line-clamp-2">
                      {service?.title}
                    </h4>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">From {service?.startingPrice}</span>
                      <div className="flex items-center space-x-1">
                        <Icon name="Star" size={14} className="text-warning fill-current" />
                        <span className="text-muted-foreground">{service?.rating}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center">
              <Button
                variant="default"
                size="lg"
                onClick={() => setShowComparison(true)}
                disabled={selectedServices?.length < 2}
                iconName="BarChart3"
                iconPosition="left"
              >
                Compare Selected Services ({selectedServices?.length})
              </Button>
            </div>
          </div>
        ) : (
          <div>
            {/* Comparison Table */}
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                Service Comparison
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowComparison(false)}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Selection
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border border-border rounded-lg">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left p-4 font-medium text-foreground border-b border-border">
                      Features
                    </th>
                    {servicesToCompare?.map((service) => (
                      <th key={service?.id} className="text-center p-4 font-medium text-foreground border-b border-border min-w-48">
                        <div className="space-y-2">
                          <div className="font-semibold">{service?.title}</div>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                            {service?.category}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures?.map((feature, index) => (
                    <tr key={feature} className={index % 2 === 0 ? 'bg-muted/20' : 'bg-card'}>
                      <td className="p-4 font-medium text-foreground border-b border-border">
                        {feature}
                      </td>
                      {servicesToCompare?.map((service) => (
                        <td key={service?.id} className="p-4 text-center text-muted-foreground border-b border-border">
                          {service?.comparisonData?.[feature] || 'N/A'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              {servicesToCompare?.map((service) => (
                <Button
                  key={service?.id}
                  variant={service?.id === currentService?.id ? "default" : "outline"}
                  iconName="FileText"
                  iconPosition="left"
                >
                  Request Quote - {service?.title}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonTool;